import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { MainLayout } from '@/components/layouts'
import { MainContent } from '@/components/content'
import { SettingSidebar } from '@/components/sidebar'
import { ButtonPrimary } from '@/components/button'
import { LoadingScreen } from '@/components/common'
import { toast } from 'react-hot-toast'
import { serverErrorMessage } from '@/services/httpService'
import {
  UserQueryKeys,
  UserSettingFieldSchema,
  useMutateUserSettingsRaw,
  useUserSettings,
  useUserSettingsSchema,
} from '@/services'

// Setting value types (mirror of the backend SettingValueType). BOOLEAN -> true/false toggle;
// STRING -> subscription-state toggle, but only when the setting's value domain really is the
// subscribed/unsubscribed pair (see isToggleable). TIMESTAMP and anything else are not rendered.
const BOOLEAN = 'BOOLEAN'
const STRING = 'STRING'

// STRING settings whose value is a subscription-state string ("subscribed"/"unsubscribed").
const SUBSCRIBED = 'subscribed'
const UNSUBSCRIBED = 'unsubscribed'

// Resolve the "on" (checked) state for a setting given its declared value type and stored value.
const isCheckedFor = (valueType: string, value: string): boolean => {
  if (valueType === BOOLEAN) {
    return value === 'true'
  }
  // STRING subscription-state settings — case-insensitive to tolerate legacy casing.
  return value.toLowerCase() === SUBSCRIBED
}

// Map a toggle position back to the wire value for a setting's declared value type.
const valueFor = (valueType: string, checked: boolean): string => {
  if (valueType === BOOLEAN) {
    return checked ? 'true' : 'false'
  }
  return checked ? SUBSCRIBED : UNSUBSCRIBED
}

const isSubscriptionState = (value: string): boolean => {
  const normalized = value.toLowerCase()
  return normalized === SUBSCRIBED || normalized === UNSUBSCRIBED
}

/**
 * Whether a schema field is a setting this page can render as a switch and write back.
 *
 * A STRING setting qualifies only when its value domain is the subscribed/unsubscribed pair. Some
 * settings are STRING without being toggles — `hiddenBadges` is a JSON array of badge URNs, and the
 * server refuses it on `PUT /user/v1.0/settings` because hiding a badge also has to evict the cached
 * projection and re-publish to search, which only `POST /badges/{urn}/visibility` does. Testing the
 * value domain rather than blocklisting the key keeps this right for the next such setting.
 */
const isToggleable = (field: UserSettingFieldSchema): boolean =>
  field.valueType === BOOLEAN || (field.valueType === STRING && isSubscriptionState(field.defaultValue))

export default function PreferencesPage() {
  const queryClient = useQueryClient()
  const { data: schema, isLoading: isSchemaLoading } = useUserSettingsSchema()
  const { data: storedValues, isLoading: isValuesLoading } = useUserSettings()
  const { mutate: mutateUserSettings, isLoading: isSaving } = useMutateUserSettingsRaw()

  // Local working copy of every toggle-able setting, keyed by wire key.
  const [values, setValues] = useState<Record<string, string>>({})
  // The same map as last seen on the server, so a save can send only what the user changed.
  const [baseline, setBaseline] = useState<Record<string, string>>({})

  const categories = useMemo(() => {
    if (!schema) return []
    return schema.categories
      .map((category) => ({
        ...category,
        settings: category.settings.filter(isToggleable),
      }))
      .filter((category) => category.settings.length > 0)
  }, [schema])

  // Seed the working copy from the stored values, defaulting from the schema when absent.
  useEffect(() => {
    if (!schema || storedValues === undefined) return
    const seeded: Record<string, string> = {}
    for (const category of schema.categories) {
      for (const field of category.settings) {
        if (!isToggleable(field)) {
          continue
        }
        const stored = storedValues[field.key]
        seeded[field.key] = stored !== undefined && stored !== null ? String(stored) : field.defaultValue
      }
    }
    setValues(seeded)
    setBaseline(seeded)
  }, [schema, storedValues])

  const onToggle = (key: string, valueType: string) => {
    setValues((current) => {
      const nextChecked = !isCheckedFor(valueType, current[key] ?? '')
      return { ...current, [key]: valueFor(valueType, nextChecked) }
    })
  }

  const onSave = () => {
    // Send only what the user changed. The endpoint merges, so an untouched key adds nothing — and
    // a key the server declines to write here (because it has its own endpoint) fails the whole
    // request, taking every real change with it.
    const changed = Object.fromEntries(Object.entries(values).filter(([key, value]) => baseline[key] !== value))
    if (Object.keys(changed).length === 0) {
      toast.success('Preferences saved')
      return
    }

    mutateUserSettings(changed, {
      onSuccess: (res) => {
        // The saved map is the new baseline, and the cached GET is now stale — without this the
        // next seed would reinstate the pre-save values.
        setBaseline(values)
        queryClient.invalidateQueries([UserQueryKeys.GET_SETTINGS])
        // A 2xx does not always mean all of it landed: the endpoint skips keys that have their own
        // dedicated endpoint rather than failing the rest, and says so. Reporting that as success
        // would tell the member something saved that did not.
        if (res?.status === 'PARTIAL') {
          toast(res.message || 'Some preferences were not saved')
        } else {
          toast.success('Preferences saved')
        }
      },
      onError: (e) => {
        toast.error(serverErrorMessage(e, 'Failed to save preferences'))
      },
    })
  }

  if (isSchemaLoading || isValuesLoading || categories.length === 0) {
    return <LoadingScreen />
  }

  return (
    <MainLayout>
      <SettingSidebar />
      <MainContent>
        <div className="mx-auto w-full max-w-2xl text-white">
          <h1 className="text-2xl md:text-3xl font-bold">Preferences</h1>
          <p className="mt-2 text-sm text-white/80">Manage your communication, privacy, and other settings.</p>

          {categories.map((category) => (
            <section key={category.category} className="mt-8">
              <h2 className="text-lg font-semibold">{category.displayName}</h2>
              <div className="mt-3 divide-y divide-white/10 rounded-2xl bg-white/5 backdrop-blur">
                {category.settings.map((field) => (
                  <PrefRow
                    key={field.key}
                    title={field.description}
                    subText={field.subText}
                    checked={isCheckedFor(field.valueType, values[field.key] ?? field.defaultValue)}
                    onChange={() => onToggle(field.key, field.valueType)}
                  />
                ))}
              </div>
            </section>
          ))}

          <div className="mt-6 flex items-center justify-end gap-3">
            <ButtonPrimary
              title={isSaving ? 'Saving…' : 'Save'}
              className="px-6"
              disabled={isSaving}
              onClick={onSave}
            />
          </div>
        </div>
        {isSaving && <LoadingScreen />}
      </MainContent>
    </MainLayout>
  )
}

function PrefRow({
  title,
  subText,
  checked,
  onChange,
}: {
  title: string
  subText?: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <div className="flex items-center justify-between p-4 md:p-6">
      <div className="pr-4">
        <div className="text-base font-medium">{title}</div>
        {subText ? <div className="text-sm text-white/70 mt-1">{subText}</div> : null}
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={[
          'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition',
          checked ? 'bg-green-500' : 'bg-white/20',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 focus:ring-offset-black/0',
        ].join(' ')}
      >
        <span
          className={[
            'inline-block h-5 w-5 transform rounded-full bg-white transition',
            checked ? 'translate-x-5' : 'translate-x-1',
          ].join(' ')}
        />
      </button>
    </div>
  )
}
